/// REACT ///
import { useState } from "react";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
/// ACTIONS ///
import { deleteShopColor } from "api/actions/shop_material";
import { updateShopColor } from "api/actions/shop_material";
/// COMPONENTS ///
import GridDeleteButton from "./delete";
/// MUI ///
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
/// MUIX ///
import { GridColumnHeaderParams } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import { GridColDef } from "@mui/x-data-grid";
import { GridEditInputCell } from "@mui/x-data-grid";
import { GridPreProcessEditCellProps } from "@mui/x-data-grid";
import { GridRowId } from "@mui/x-data-grid";
import { GridRowModel } from "@mui/x-data-grid";
/// COLORS ///
import { error_color } from "styles/style";
import { primary_color } from "styles/style";
import { text_primary } from "styles/style";
import { white_color } from "styles/style";

export default function FullFeaturedCrudGrid() {
  const dispatch = useAppDispatch();
  const [toast_open, setToastOpen] = useState<boolean>(false);
  const [toast_message, setToastMessage] = useState<string>("");
  const { colors } = useAppSelector(state => state.shop_material);

  const handleDeleteClick = (id: GridRowId) => {
    dispatch(deleteShopColor(Number(id)));
    setToastMessage("Color delete");
  };

  const processRowUpdate = (new_row: GridRowModel) => {
    dispatch(updateShopColor(
      {
        ...new_row,
        price: Math.round(new_row.price * 100) / 100,
        width: Math.round(new_row.width * 100) / 100,
        length: Math.round(new_row.length * 100) / 100,
        amount: Math.round(new_row.amount)
      },
      new_row.id
    ));
    setToastMessage(`${new_row.name} Updated`);
    setToastOpen(true);
    return new_row;
  };

  const processRowUpdateError = () => {

  }


  const columns: GridColDef[] = [
    {
      field: "name",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Name"}
        </strong>
      ),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const has_error = !params.props.value || params.props.value === "";
        return { ...params.props, error: has_error };
      },
      flex: 2,
      editable: true,
      renderEditCell: params => {
        return <GridEditInputCell {...params} disabled={params.row.required} />
      }
    },
    {
      field: "price",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Price/SQFT ($)"}
        </strong>
      ),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const has_error: boolean = !params.props.value || isNaN(Number(params.props.value)) || Number(params.props.value) < 0;
        return { ...params.props, error: has_error };
      },
      valueFormatter: (value?: number) => {
        if (!value) {
          const zero: number = 0;
          return zero.toFixed(2);
        }
        return `${value.toFixed(2)}`;
      },
      type: "number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      editable: true,
    },
    {
      field: "width",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Width (\") "}
        </strong>
      ),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const has_error: boolean = !params.props.value || isNaN(Number(params.props.value)) || Number(params.props.value) < 0;
        return { ...params.props, error: has_error };
      },
      valueFormatter: (value?: number) => {
        if (!value) {
          const zero: number = 0;
          return zero.toFixed(2);
        }
        return `${value.toFixed(2)}`;
      },
      headerName: "Width",
      type: "number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      editable: true,
      renderEditCell: params => {
        return <GridEditInputCell {...params} disabled={params.row.required} />
      }
    },
    {
      field: "length",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Length (\") "}
        </strong>
      ),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const has_error: boolean = !params.props.value || isNaN(Number(params.props.value)) || Number(params.props.value) < 0;
        return { ...params.props, error: has_error };
      },
      valueFormatter: (value?: number) => {
        if (!value) {
          const zero: number = 0;
          return zero.toFixed(2);
        }
        return `${value.toFixed(2)}`;
      },
      flex: 1,
      editable: true,
      align: "left",
      headerAlign: "left",
      type: "number",
      renderEditCell: params => {
        return <GridEditInputCell {...params} disabled={params.row.required} />
      }
    },
    {
      field: "amount",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Amount"}
        </strong>
      ),
      valueFormatter: (value?: number) => {
        if (!value) {
          const zero: number = 0;
          return zero.toFixed(0);
        }
        return `${value.toFixed(0)}`;
      },
      flex: 1,
      editable: true,
      align: "left",
      headerAlign: "left",
      type: "number",
      renderEditCell: params => {
        return <GridEditInputCell {...params} disabled={params.row.required} />
      }
    },
    {
      field: "required",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Standard"}
        </strong>
      ),
      flex: 1,
      align: "center",
      headerAlign: "center",
      type: "boolean",
      editable: true
    },

    {
      field: "actions",
      type: "actions",
      align: "right",
      headerAlign: "right",
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          {"Actions"}
        </strong>
      ),
      flex: 1,
      cellClassName: "actions",
      getActions: ({ id }) => {
        return [
          <GridDeleteButton
            id={id}
            handleDeleteClick={(item_id: string | number) => handleDeleteClick(item_id)}
          />
        ];
      },
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <DataGrid
        rows={colors}
        columns={columns}
        autoHeight
        density="compact"
        editMode="row"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={processRowUpdateError}
        sx={{
          '& .Mui-error': {
            backgroundColor: 'rgb(126,10,15, 0.1)',
            color: error_color,
            paddingBottom: "50px"
          },
        }}
      />
      <Snackbar
        open={toast_open}
        sx={{ marginLeft: "80px", marginBottom: "50px" }}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        message={toast_message}>
        <Alert
          icon={false}
          sx={{ backgroundColor: white_color, color: text_primary, border: `1px solid ${primary_color}` }}
        >
          {toast_message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
